import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Autocomplete,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  useTagTypes,
  useTagValues,
  useCaseTags,
  useAddCaseTag,
  useRemoveCaseTag,
} from "../../../api/tags/tags";
import { useSnackbar } from "../../../context/SnackbarContext";

function buildTypePath(typeId, types, sep = " → ") {
  const typeMap = {};
  types.forEach((t) => (typeMap[t.id] = t));
  let path = [];
  let current = typeMap[typeId];
  while (current) {
    path.unshift(current.name);
    current = current.parent ? typeMap[current.parent] : null;
  }
  return path.join(sep);
}

function getTypeDepth(typeId, types) {
  const typeMap = {};
  types.forEach((t) => (typeMap[t.id] = t));
  let depth = 0;
  let current = typeMap[typeId];
  while (current && current.parent) {
    depth += 1;
    current = typeMap[current.parent];
  }
  return depth;
}

export function CaseTagEditor({ caseId }) {
  const { data: types = [], isLoading: loadingTypes } = useTagTypes();
  const { data: tagValues = [], isLoading: loadingValues } = useTagValues();
  const {
    data: caseTags = [],
    isLoading: loadingCaseTags,
    refetch,
  } = useCaseTags(caseId);

  const addCaseTag = useAddCaseTag(caseId);
  const removeCaseTag = useRemoveCaseTag(caseId);
  const { showSnackbar } = useSnackbar();

  const assigned = useMemo(
    () =>
      caseTags
        .map((ct) => tagValues.find((tv) => tv.id === ct.tag_value_id))
        .filter(Boolean),
    [caseTags, tagValues]
  );
  const assignedIds = useMemo(
    () => new Set(assigned.map((v) => v.id)),
    [assigned]
  );

  const optionsSorted = useMemo(() => {
    return tagValues
      .filter((tv) => !assignedIds.has(tv.id))
      .sort((a, b) => {
        const pathA = buildTypePath(a.type, types);
        const pathB = buildTypePath(b.type, types);
        if (pathA < pathB) return -1;
        if (pathA > pathB) return 1;
        return a.name.localeCompare(b.name);
      });
  }, [tagValues, assignedIds, types]);

  const [pending, setPending] = useState([]);
  const [isApplying, setIsApplying] = useState(false);

  useEffect(() => {
    setPending([]);
  }, [caseId, caseTags, tagValues]);

  const handleAdd = (event, values) => {
    const newTag = values[values.length - 1];
    if (newTag) {
      setPending((prev) =>
        prev.find((v) => v.id === newTag.id) ? prev : [...prev, newTag]
      );
    }
  };

  const handleRemoveAssigned = async (tag) => {
    const ct = caseTags.find((ct) => ct.tag_value_id === tag.id);
    if (!ct) return;
    try {
      await removeCaseTag.mutateAsync(ct.id);
      showSnackbar("Tag removed!", "success");
      refetch();
    } catch (e) {
      showSnackbar(
        e?.response?.data?.message || "Failed to remove tag.",
        "error"
      );
    }
  };

  const handleRemovePending = (tag) => {
    setPending((prev) => prev.filter((v) => v.id !== tag.id));
  };

  const handleApply = async () => {
    setIsApplying(true);
    try {
      for (const tagValue of pending) {
        await addCaseTag.mutateAsync({
          tag_type_id: tagValue.type,
          tag_value_id: tagValue.id,
        });
      }
      showSnackbar("Tags added!", "success");
      setPending([]);
      refetch();
    } catch (e) {
      showSnackbar(
        e?.response?.data?.message || "Failed to add tags.",
        "error"
      );
    }
    setIsApplying(false);
  };

  if (loadingTypes || loadingValues || loadingCaseTags) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box mt={4}>
      {assigned.length > 0 && (
        <Typography variant="subtitle1" gutterBottom>
          Assigned Case Tags
        </Typography>
      )}
      <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
        {assigned.map((tag) => (
          <Chip
            key={tag.id}
            label={`${buildTypePath(tag.type, types)}: ${tag.name}`}
            onDelete={() => handleRemoveAssigned(tag)}
            color="primary"
            variant="outlined"
            deleteIcon={<DeleteIcon />}
          />
        ))}
      </Box>

      <Typography variant="subtitle2" gutterBottom>
        Add More Case Tags (Optional)
      </Typography>
      <Autocomplete
        multiple
        options={optionsSorted}
        groupBy={(option) => buildTypePath(option.type, types)}
        getOptionLabel={(option) =>
          `${buildTypePath(option.type, types)}: ${option.name}`
        }
        renderOption={(props, option) => {
          const depth = getTypeDepth(option.type, types);
          return (
            <Box component="li" {...props} sx={{ pl: depth * 2 }}>
              {`${buildTypePath(option.type, types)}: ${option.name}`}
            </Box>
          );
        }}
        value={pending}
        onChange={handleAdd}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select case tags to add"
            variant="outlined"
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              label={`${buildTypePath(option.type, types)}: ${option.name}`}
              {...getTagProps({ index })}
              onDelete={() => handleRemovePending(option)}
              color="secondary"
              variant="outlined"
            />
          ))
        }
        loading={loadingTypes || loadingValues}
        disableCloseOnSelect
        fullWidth
        sx={{ mb: 2 }}
      />

      <Button
        variant="contained"
        color="success"
        onClick={handleApply}
        disabled={!pending.length || isApplying}
      >
        {isApplying ? "Applying..." : "Apply Case Tags"}
      </Button>
    </Box>
  );
}
